/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import config from "../config/userLists.config.json";
import settingConfig from "../config/settings.config.json";
import { StorageModule } from "@vlinder-web/storage-module-react";
import jwt_decode from "jwt-decode";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import {
  GET_MASQUERADER_LIST,
  GET_MASQUERADER_LIST_ADMIN,
  GET_USER_LIST,
} from "../graphql";
import { VApplication } from "../../../application";
import moment from "moment-timezone";
import { pretty } from "@vlinder-web/utils-react";
import { GET_PRESENTATIONS } from "../../InviteMasqueraderListPageService/graphql";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IMasqueraderListsMethods extends IPluginMethodMap {
  getMasqueraderListsProps(args: any): Promise<any>;
}

export class MasqueraderListsService implements IAgentPlugin {
  readonly methods: IMasqueraderListsMethods;

  constructor() {
    this.methods = {
      getMasqueraderListsProps: this.getMasqueraderListsProps.bind(this),
    };
  }

  async getMasqueraderListsProps(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    let filter = {},
      defaultPresentation: any = "";
    const searchType = this.determineSearchType(args?.criteria);
    if (searchType && searchType !== null) {
      if (searchType === "multi") {
        filter = this.getMultiFilter(args?.criteria);
      } else {
        filter = this.convertCriteria(args?.criteria);
      }
    }
    let checkPaymentStats = (filter: any) => {
      if (
        filter.hasOwnProperty("paymentStats") &&
        (filter.paymentStats.hasOwnProperty("contains") ||
          filter.paymentStats.hasOwnProperty("eq"))
      ) {
        return (
          filter.paymentStats.contains === "UnPaid" ||
          filter.paymentStats.eq === "UnPaid"
        );
      }
      return false;
    };

    console.log("filter==>", pretty(filter));
    const options = {
      limit: args?.size ?? 10,
      skip: args?.offset ?? 0,
      sort: {
        field: "createdAt",
        order: checkPaymentStats(filter) ? "asc" : "desc",
      },
    };
    try {
      if (client) {
        const presentation = await client.query({
          query: GET_PRESENTATIONS,
          variables: {
            filter: {},
            options: {
              limit: 10,
              skip: 0,
              sort: {
                field: "status",
                order: "asc",
              },
            },
          },
          fetchPolicy: "network-only",
        });
        if (
          presentation &&
          Array.isArray(presentation?.data?.getPresentations?.content) &&
          presentation?.data?.getPresentations?.content[0]?.id
        ) {
          defaultPresentation =
            presentation?.data?.getPresentations?.content[0]?.id;
          filter.presentation = {
            eq: args?.criteria?.presentation ?? defaultPresentation,
          };
        }
        const storageSrv = StorageModule.getInstance();
        let _sessionToken = storageSrv.get(
          "userProfile",
          "session"
        )?.accessToken;
        let _localToken = storageSrv.get("userProfile", "local")?.accessToken;
        let _token = _localToken ?? _sessionToken;
        const tokenRole = jwt_decode(_token);
        const res = await client.query({
          query:
            tokenRole?.currentRole?.name === "production"
              ? GET_MASQUERADER_LIST
              : GET_MASQUERADER_LIST_ADMIN,
          variables: {
            options: options,
            filter: filter,
          },
          fetchPolicy: "network-only",
        });
        if (res?.data) {
          let _rowData = this.formatUserTable(
            res?.data?.getUserAdvancedSearch?.content
          );

          let _totalPages = res?.data?.getUserAdvancedSearch?.count
            ? Math.ceil(parseInt(res?.data?.getUserAdvancedSearch?.count) / 10)
            : 1;

          let _currentPage =
            res?.data?.getUserAdvancedSearch?.skip /
              res?.data?.getUserAdvancedSearch?.limit +
            1;
          let partitionArray = this.formatPartition(
            res?.data?.getUserAdvancedSearch?.content
          );
          const settings = this.getTransformConfig(
            settingConfig,
            partitionArray,
            tokenRole
          );

          return {
            ...config,
            settingConfig: settings,
            ultUsers: _rowData,
            isEmpty: res?.data?.getUserAdvancedSearch?.content?.length <= 0,
            activePage: _currentPage,
            currentPage: _currentPage,
            pageSize: res?.data?.getUserAdvancedSearch?.limit,
            totalCount: res?.data?.getUserAdvancedSearch?.count,
            totalPages: _totalPages,
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  private getTransformConfig(data: any, partition?: any[], role?: any) {
    // if (role?.currentRole?.name === "production") {
    data.tableHeaders[2].filterTypes[1].options = partition;
    // }
    data.tableHeadersAdmin[5].filterTypes[1].options = partition;
    return data;
  }

  private formatPartition(data: any) {
    let _rowData: any = [];
    data &&
      data.forEach((item?: any) => {
        _rowData.push(item?.Referal?.partition);
      });
    const partitionArray = Array.from(new Set(_rowData));
    let array: any[] = [];
    partitionArray &&
      partitionArray.forEach((item: any) => {
        array.push({
          value: item,
          label: item,
        });
      });
    console.log("partition==>", array);
    return array;
  }

  private formatUserTable(data: any) {
    const RECENT_USER_MS = 24 * 60 * 60 * 1000;
    const now = Date.now();
    let _rowData: any = [];
    data.forEach((item: any) => {
      const invitedBy = item?.ReferalUsers?.[0] ?? data?.User;

      const fullName = invitedBy?.fullName?.trim();
      const label = fullName || invitedBy?.email || "Masq Camp";
      const createdAtRaw = item?.createdAt ?? null;

      _rowData.push({
        id: item?.User?._id,
        name: item?.User?.fullName ?? "Null Null",
        email: item?.User?.email ?? "null",
        note: item?.note ?? "-",
        gender: item?.User?.gender ?? "-",
        invitedBy: label,
        country: item?.User?.address?.country ?? "-",
        totalAmount: item?.totalAmount ?? "-",
        section: item?.Section?.name ? item?.Section?.name : "_",
        sectionOption: item?.CostumeType?.name ? item?.CostumeType?.name : "_",
        paymentStatus: item?.paymentStats ? item?.paymentStats : "-",
        costumeID: item?.costumeID ? item?.costumeID : "-",
        masqId: item?.User?.vuid ? item?.User?.vuid : "-",
        createdAt: createdAtRaw
          ? this.formatCreatedDate(createdAtRaw)
          : "-",
        createdAtRaw,
        isRecentlyCreated: createdAtRaw
          ? now - new Date(createdAtRaw).getTime() <= RECENT_USER_MS
          : false,
        boxStatus: item?.boxStatus ? this.getBoxStatus(item?.boxStatus) : "-",
        allDetails: item,
      });
    });
    return _rowData;
  }

  private getBoxStatus(boxStatus: string) {
    if (boxStatus) {
      if (boxStatus == "PartialPurchased") {
        return "Partially Paid";
      } else if (boxStatus == "Purchased") {
        return "Paid";
      }
      return boxStatus;
    }
  }

  private determineSearchType(criteria: any) {
    if (Object.keys(criteria).length === 0) {
      return null;
    } else if (Object.keys(criteria)[0] === "0") {
      return "multi";
    } else {
      return "normal";
    }
  }

  private convertCriteria(criteria: any) {
    const result: any = {};
    for (const key in criteria) {
      if (key === "presentation" || key === "paymentStats") {
        result[key] = { eq: criteria[key] };
      } else {
        result[key] = { contains: criteria[key] };
      }
    }
    return result;
  }

  private getMultiFilter(criteria: any) {
    const transformedCriteria: any = {};
    for (const key in criteria) {
      if (criteria.hasOwnProperty(key)) {
        const { key: filterKey, filters } = criteria[key];
        const { inputData } = filters;
        const filterType = Object.keys(filters).find(
          (key) => key !== "inputData"
        );

        let filterValue = filters[filterType];

        // Convert "true" and "false" strings to actual boolean values
        if (
          typeof filterValue === "string" &&
          filterValue.toLowerCase() === "true"
        ) {
          filterValue = true;
        } else if (
          typeof filterValue === "string" &&
          filterValue.toLowerCase() === "false"
        ) {
          filterValue = false;
        }

        transformedCriteria[inputData.id] = {
          [filterType.toLowerCase().replace("equal", "eq")]: filterValue,
        };
      }
    }
    return transformedCriteria;
  }

  private formatCreatedDate(date: Date) {
    if (date) {
      return moment(date).format("LL");
    }
  }
}
